/**
 * Vehicle related utility functions
 */

import { toDateString } from "@fm/ui";

export interface SleepingArrangements {
  kingBeds: number;
  queenBeds: number;
  fullBeds: number;
  twinBeds: number;
}

export interface CalendarDayData {
  date: string;
  available: boolean;
  rate: number | null;
  currency: string;
}

/**
 * Parses the vehicle description to extract sleeping arrangement counts
 * Looks for patterns like "1 King bed", "2 Twin beds", etc.
 */
export function parseSleepingArrangements(
  description: string | null | undefined,
): SleepingArrangements {
  const result = {
    kingBeds: 0,
    queenBeds: 0,
    fullBeds: 0,
    twinBeds: 0,
  };

  if (!description) return result;

  const kingBedsMatch = description.match(/(\d+)\s+King\s+bed/i);
  if (kingBedsMatch && kingBedsMatch[1])
    result.kingBeds = parseInt(kingBedsMatch[1], 10);

  const queenBedsMatch = description.match(/(\d+)\s+Queen\s+bed/i);
  if (queenBedsMatch && queenBedsMatch[1])
    result.queenBeds = parseInt(queenBedsMatch[1], 10);

  const fullBedsMatch = description.match(/(\d+)\s+Full\s+bed/i);
  if (fullBedsMatch && fullBedsMatch[1])
    result.fullBeds = parseInt(fullBedsMatch[1], 10);

  const twinBedsMatch = description.match(/(\d+)\s+Twin\s+bed/i);
  if (twinBedsMatch && twinBedsMatch[1])
    result.twinBeds = parseInt(twinBedsMatch[1], 10);

  return result;
}

/**
 * Calculates the average nightly rate for a date range using calendar data
 *
 * @param calendarData - Map of date strings to calendar day data with rates
 * @param fromDate - Start date (ISO format YYYY-MM-DD) or undefined
 * @param toDate - End date (ISO format YYYY-MM-DD) or undefined
 * @returns Average nightly rate, or 0 if no data available
 */
export function calculateAverageNightlyRate(
  calendarData: Map<string, CalendarDayData>,
  fromDate: string | undefined,
  toDate: string | undefined,
): number {
  if (!fromDate || !toDate) return 0;

  const nightlyRates: number[] = [];
  const startDate = new Date(fromDate);
  const endDate = new Date(toDate);

  const currentDate = new Date(startDate);
  while (currentDate < endDate) {
    const dateStr: string = toDateString(currentDate);
    const dayData = calendarData.get(dateStr);

    if (dayData?.rate !== null && dayData?.rate !== undefined) {
      nightlyRates.push(dayData.rate);
    }

    currentDate.setDate(currentDate.getDate() + 1);
  }

  if (nightlyRates.length === 0) return 0;

  const total = nightlyRates.reduce((sum, rate) => sum + rate, 0);
  return total / nightlyRates.length;
}
